import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthScreen } from '../components/AuthScreen';
import Logo from '../components/Logo';
import Sidebar from '../components/Sidebar';
import Toast, { ToastType } from '../components/Toast';
import Topbar from '../components/Topbar';
import { accountSidebarSection, getDefaultRoute, getSidebarSections } from '../config/navigation';
import { AppRoutes, routeTitles } from '../config/routes';
import { FleetInvitationAcceptance } from '../pages/fleet/FleetInvitationAcceptance';
import { attemptLogin, clearAuth, isFleetManagerKycApproved, isSignupEmailVerified, loadStoredAuth, requestPasswordReset, resendSignupVerification, resetPassword, saveAuth, signupFleetManager, verifySignupEmail } from '../services/auth';
import { FleetManagerSignupForm, ForgotPasswordForm, ResetPasswordForm, VerifyEmailForm } from '../components/AuthScreen';
import { getKycQueue } from '../services/admin';
import { AppState } from '../types/app';

const KYC_PENDING_COUNT_EVENT = 'responseops.kyc-pending-count';
const VERIFIED_EMAILS_STORAGE_KEY = 'responseops.verified-emails';

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [appState, setAppState] = useState<AppState>(() => loadStoredAuth());
  const [loginForm, setLoginForm] = useState({
    identifier: '',
    password: '',
  });
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isPasswordResetting, setIsPasswordResetting] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [kycPendingCount, setKycPendingCount] = useState<number>();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => isMobileViewport(),
  );
  const [toast, setToast] = useState<{ message: string; type: ToastType; visible: boolean }>({
    message: 'Welcome to DailyMove',
    type: 'success',
    visible: false,
  });

  const showToast = React.useCallback((message: string, type: ToastType = 'success') => {
    setToast({ message, type, visible: true });
    window.setTimeout(() => {
      setToast((current) => ({ ...current, visible: false }));
    }, 2600);
  }, []);

  useEffect(() => {
    if (appState.user && location.pathname !== appState.currentRoute) {
      setAppState((current) => ({ ...current, currentRoute: location.pathname }));
    }
  }, [appState.currentRoute, appState.user, location.pathname]);

  useEffect(() => {
    const canUseFleetDashboard = appState.demoMode || isFleetManagerKycApproved(appState);
    const isRestrictedFleetRoute = location.pathname.startsWith('/fleet/')
      && location.pathname !== '/fleet/onboarding'
      && location.pathname !== '/fleet-invitations';

    if (appState.user && appState.role === 'fleet_manager' && !canUseFleetDashboard && isRestrictedFleetRoute) {
      navigate('/fleet/onboarding', { replace: true });
      setAppState((current) => ({ ...current, currentRoute: '/fleet/onboarding' }));
    }
  }, [appState, location.pathname, navigate]);

  useEffect(() => {
    setSearchQuery('');
  }, [location.pathname]);

  useEffect(() => {
    const isAdmin = appState.role === 'admin' || appState.role === 'super_admin';

    if (!isAdmin) {
      setKycPendingCount(undefined);
      return undefined;
    }

    let isActive = true;

    if (appState.demoMode) {
      setKycPendingCount(4);
    } else {
      getKycQueue()
        .then((response) => {
          if (isActive) {
            setKycPendingCount(response.submissions.filter((submission) => submission.status.toLowerCase() === 'pending').length);
          }
        })
        .catch(() => {
          if (isActive) {
            setKycPendingCount(0);
          }
        });
    }

    const handlePendingCount = (event: Event) => {
      const count = (event as CustomEvent<number>).detail;
      if (typeof count === 'number') {
        setKycPendingCount(count);
      }
    };

    window.addEventListener(KYC_PENDING_COUNT_EVENT, handlePendingCount);
    return () => {
      isActive = false;
      window.removeEventListener(KYC_PENDING_COUNT_EVENT, handlePendingCount);
    };
  }, [appState.demoMode, appState.role]);

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const mobileQuery = window.matchMedia('(max-width: 700px)');
    const handleViewportChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsSidebarCollapsed(true);
      }
    };

    mobileQuery.addEventListener('change', handleViewportChange);
    return () => mobileQuery.removeEventListener('change', handleViewportChange);
  }, []);

  useEffect(() => {
    const handleAuthRefreshed = (event: Event) => {
      const detail = (event as CustomEvent<{ accessToken: string; refreshToken: string }>).detail;

      if (detail) {
        setAppState((current) => ({
          ...current,
          accessToken: detail.accessToken,
          refreshToken: detail.refreshToken,
        }));
      }
    };

    const handleAuthExpired = (event: Event) => {
      const message = (event as CustomEvent<{ message?: string }>).detail?.message;
      clearAuth(setAppState);
      navigate('/fleet/dashboard');
      showToast(message ?? 'Your session is no longer active. Please sign in again.', message ? 'success' : 'error');
    };

    window.addEventListener('responseops.auth-refreshed', handleAuthRefreshed);
    window.addEventListener('responseops.auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('responseops.auth-refreshed', handleAuthRefreshed);
      window.removeEventListener('responseops.auth-expired', handleAuthExpired);
    };
  }, [navigate, showToast]);

  const goToRoute = (route: string) => {
    navigate(route);
    setAppState((current) => ({ ...current, currentRoute: route }));

    if (isMobileViewport()) {
      setIsSidebarCollapsed(true);
    }
  };

  const currentUser = appState.user ?? {
    id: 'anonymous',
    email: '',
    name: 'Guest User',
    initials: 'GU',
  };
  const roleLabel = appState.role ? appState.role.replace('_', ' ') : 'Signed out';
  const isFleetKycApproved = appState.demoMode || isFleetManagerKycApproved(appState);
  const sidebarSections = appState.role === 'fleet_manager' && !isFleetKycApproved
    ? [accountSidebarSection]
    : getSidebarSections(appState.role, kycPendingCount);

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSigningIn) {
      return;
    }

    if (!loginForm.identifier.trim() || !loginForm.password) {
      showToast('Enter your  username and password', 'error');
      return;
    }

    setIsSigningIn(true);

    try {
      const session = await attemptLogin({
        identifier: loginForm.identifier.trim(),
        password: loginForm.password,
      });
      if (!isSignupEmailVerified(session) && !isLocallyVerifiedEmail(session.user.email)) {
        setPendingVerificationEmail(session.user.email || loginForm.identifier.trim());
        setLoginForm((current) => ({ ...current, password: '' }));
        showToast('Verify your email before signing in.', 'error');
        return;
      }

      saveAuth(session, setAppState);
      navigate(session.role === 'fleet_manager' && !isFleetManagerKycApproved(session) ? '/fleet/onboarding' : getDefaultRoute(session.role));
      showToast('Signed in successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to sign in';
      showToast(message, 'error');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleFleetManagerSignup = async (form: FleetManagerSignupForm) => {
    if (isSigningUp) {
      throw new Error('Signup is already in progress');
    }

    setIsSigningUp(true);

    try {
      const result = await signupFleetManager({
        username: form.username.trim(),
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        password: form.password,
      });
      setLoginForm({ identifier: form.username.trim(), password: '' });
      showToast('Fleet manager account created');
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to create fleet manager account';
      showToast(message, 'error');
      throw error;
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleRequestPasswordReset = async (form: ForgotPasswordForm) => {
    if (isPasswordResetting) {
      throw new Error('Password reset is already in progress');
    }

    setIsPasswordResetting(true);

    try {
      await requestPasswordReset(form.email.trim(), form.role);
      showToast('Reset code sent if the email exists');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to request password reset';
      showToast(message, 'error');
      throw error;
    } finally {
      setIsPasswordResetting(false);
    }
  };

  const handleVerifySignupEmail = async (form: VerifyEmailForm) => {
    try {
      await verifySignupEmail(form.email.trim(), form.code.trim());
      rememberVerifiedEmail(form.email.trim());
      setPendingVerificationEmail('');
      setLoginForm({ identifier: form.email.trim(), password: '' });
      showToast('Email verified successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to verify email';
      showToast(message, 'error');
      throw error;
    }
  };

  const handleResendSignupVerification = async (email: string) => {
    if (!email.trim()) {
      throw new Error('Enter your email address first.');
    }

    try {
      await resendSignupVerification(email.trim());
      showToast('Verification code resent');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to resend verification code';
      showToast(message, 'error');
      throw error;
    }
  };

  const handleResetPassword = async (form: ResetPasswordForm) => {
    if (isPasswordResetting) {
      throw new Error('Password reset is already in progress');
    }

    setIsPasswordResetting(true);

    try {
      await resetPassword(form.email.trim(), form.code.trim(), form.password, form.role);
      setLoginForm({ identifier: form.email.trim(), password: '' });
      showToast('Password reset successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to reset password';
      showToast(message, 'error');
      throw error;
    } finally {
      setIsPasswordResetting(false);
    }
  };

  const handleFleetKycSubmitted = React.useCallback((status = 'pending') => {
    let changed = false;

    setAppState((current) => {
      if (!current.user) {
        return current;
      }

      const previousStatus = current.user.kycStatus ?? current.user.onboardingStatus ?? '';
      if (previousStatus === status) {
        return current;
      }

      changed = true;
      const updatedState = {
        ...current,
        user: {
          ...current.user,
          kycStatus: status,
          onboardingRequired: !['approved', 'completed', 'verified'].includes(status),
          onboardingStatus: status,
        },
      };
      updateStoredUser(updatedState.user);
      return updatedState;
    });

    if (changed) {
      showToast(
        ['approved', 'completed', 'verified'].includes(status)
          ? 'KYC approved. Fleet dashboard unlocked.'
          : 'KYC status updated',
      );
    }
  }, [showToast]);

  const handleClearAuth = () => {
    clearAuth(setAppState);
    navigate('/fleet/dashboard');
    showToast('Signed out');
  };

  if (window.location.pathname === '/fleet-invitations') {
    return <FleetInvitationAcceptance />;
  }

  if (!appState.user) {
    return (
      <AuthScreen
        isSubmitting={isSigningIn}
        isPasswordResetSubmitting={isPasswordResetting}
        isSignupSubmitting={isSigningUp}
        loginForm={loginForm}
        onChangeLoginForm={setLoginForm}
        onFleetManagerSignup={handleFleetManagerSignup}
        onRequestPasswordReset={handleRequestPasswordReset}
        onResendSignupVerification={handleResendSignupVerification}
        onResetPassword={handleResetPassword}
        onSubmit={handleLoginSubmit}
        onVerifySignupEmail={handleVerifySignupEmail}
        toast={toast}
        verificationEmail={pendingVerificationEmail}
      />
    );
  }

  return (
    <Box className={`portal-shell${isSidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      {!isSidebarCollapsed ? (
        <button
          aria-label="Close sidebar"
          className="sidebar-backdrop"
          onClick={() => setIsSidebarCollapsed(true)}
          type="button"
        />
      ) : null}
      <Sidebar
        activeItem={appState.currentRoute}
        brand={<Logo />}
        isCollapsed={isSidebarCollapsed}
        onLogout={handleClearAuth}
        onSelect={goToRoute}
        sections={sidebarSections}
        user={{
          name: currentUser.name,
          role: roleLabel,
          initials: currentUser.initials,
        }}
      />

      <Box className="main" component="main">
        <Topbar
          accessToken={appState.accessToken}
          demoMode={appState.demoMode}
          isSidebarCollapsed={isSidebarCollapsed}
          onSearchChange={setSearchQuery}
          onToggleSidebar={() => setIsSidebarCollapsed((current) => !current)}
          role={appState.role || 'signed_out'}
          searchValue={searchQuery}
          title={routeTitles[location.pathname] ?? 'Operations Dashboard'}
        />

        <Box className="content-area" component="section">
          <AppRoutes accessToken={appState.accessToken} demoMode={appState.demoMode} fleetKycApproved={isFleetKycApproved} onFleetKycSubmitted={handleFleetKycSubmitted} role={appState.role} searchQuery={searchQuery} user={appState.user} />
        </Box>
      </Box>

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
    </Box>
  );
}

function isMobileViewport() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 700px)').matches;
}

function updateStoredUser(user: AppState['user']) {
  try {
    const stored = window.localStorage.getItem('responseops.auth');
    if (!stored) return;

    const session = JSON.parse(stored);
    if (!session || typeof session !== 'object') return;

    window.localStorage.setItem('responseops.auth', JSON.stringify({ ...session, user }));
  } catch {
    // If storage is unavailable, the in-memory state still reflects the submission.
  }
}

function getVerifiedEmails() {
  try {
    const stored = window.localStorage.getItem(VERIFIED_EMAILS_STORAGE_KEY);
    const emails = stored ? JSON.parse(stored) : [];
    return Array.isArray(emails) ? emails.filter((email): email is string => typeof email === 'string') : [];
  } catch {
    return [];
  }
}

function isLocallyVerifiedEmail(email: string) {
  const normalizedEmail = email.trim().toLowerCase();
  return Boolean(normalizedEmail) && getVerifiedEmails().includes(normalizedEmail);
}

function rememberVerifiedEmail(email: string) {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) return;

  const emails = new Set(getVerifiedEmails());
  emails.add(normalizedEmail);
  window.localStorage.setItem(VERIFIED_EMAILS_STORAGE_KEY, JSON.stringify(Array.from(emails)));
}
